"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  enableOutboundCallingPhoneNumberAction,
  startOutgoingVoiceCallAction,
} from "@/server-actions/outgoing-voice-calls"
import type { EnabledOutboundPhoneNumber, OutgoingVoiceCallResult } from "@/lib/voice/outgoing-calls"
import { withQueryHandling } from "@/lib/with-query-handling"
import { outgoingVoiceCallPhoneNumbersQueryKey } from "@/hooks/queries/outgoing-voice-calls"
import { pbxPhoneNumbersQueryKey } from "@/hooks/queries/pbx-phone-numbers"
import type { StartOutgoingVoiceCallInput } from "@/lib/voice/outgoing-call-validation"

type StartOutgoingVoiceCallMutationInput = StartOutgoingVoiceCallInput & {
  setErrors?: (errors: Record<string, unknown>) => void
}

export function useStartOutgoingVoiceCallMutation() {
  return useMutation({
    mutationFn: async ({
      setErrors,
      ...input
    }: StartOutgoingVoiceCallMutationInput): Promise<OutgoingVoiceCallResult> => {
      const result = await withQueryHandling(startOutgoingVoiceCallAction(input), {
        loading: "Starting outgoing call...",
        success: "Outgoing call started",
        setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useEnableOutboundCallingPhoneNumberMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { phoneNumber: string }): Promise<EnabledOutboundPhoneNumber> => {
      const result = await withQueryHandling(enableOutboundCallingPhoneNumberAction(input), {
        loading: "Enabling outbound calling...",
        success: "Outbound calling enabled",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: outgoingVoiceCallPhoneNumbersQueryKey }),
        queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey }),
      ])
    },
  })
}
